import React, { FC, useEffect, useState } from 'react';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import useLocales from 'hooks/useLocales';
import Navbar from 'components/Navbar';
import ActionButton from 'components/ActionButton/Index';
import MatchingProfile from 'components/CompanyTabs/MatchingProfile/Index';
import { companyHeader } from 'core/constants/companyHeader';
import { COMPANY_ROUTES } from 'core/constants/companyRoutes';
import { CompanyTabEnum } from 'core/enum/companyTab';
import PostAJob from 'components/CompanyTabs/PostAJob/Index';
import CultureProfile from 'components/CompanyTabs/CultureProfile/Index';
import CandidateOverview from 'components/CompanyTabs/CandidateOverview/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import AvatarPopup from 'components/shared/Popups/Avatar/Index';
import CareerPage from 'components/CareerPage/Index';
import { IMe } from 'core/interface/me';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';

import CompanyAvatar from 'components/shared/CompanyAvatar/Index';
import Footer from 'components/Footer/Index';
import { InvitationsService } from 'services/invitations/invitations';

import './Index.scss';
import { isValidEmail } from 'utils/isValidEmail';
import { CompanyService } from 'services/companes/companies';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const PATH_TO_TAB: Record<string, CompanyTabEnum> = {
  [COMPANY_ROUTES.FIND_CANDIDATES]: CompanyTabEnum.FIND_CANDIDATES,
  [COMPANY_ROUTES.JOB_POSTS]: CompanyTabEnum.JOB_POSTS,
  [COMPANY_ROUTES.CULTURE_PROFILES]: CompanyTabEnum.CULTURE_PROFILES,
  [COMPANY_ROUTES.CANDIDATE_OVERVIEW]: CompanyTabEnum.CANDIDATE_OVERVIEW,
  [COMPANY_ROUTES.CAREER_PAGE]: CompanyTabEnum.CAREER_PAGE
};

const Company: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useSelector((state: IMe) => state.user).user;
  const { companies } = useSelector((state: RootState) => state.company);
  const activeTab = PATH_TO_TAB[pathname] ?? CompanyTabEnum.FIND_CANDIDATES;
  const [showAvatarPopup, setShowAvatarPopup] = useState<boolean>();
  const [companyEditModal, setCompanyEditModal] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteEmailError, setInviteEmailError] = useState<string>('');

  const isAdmin = me?.role && me?.role === 'admin';

  useEffect(() => {
    CompanyService.getCompanies(me?.company);
  }, [me?.company]);

  const handleInviteInputChange = (e: string): void => {
    if (e.length > 0) {
      setInviteEmail(e);
      if (!isValidEmail(e)) {
        setInviteEmailError(t('Invalid email'));
      } else {
        setInviteEmailError('');
      }
    } else {
      setInviteEmailError('');
    }
  };

  const handleClosePopup = () => {
    setShowAvatarPopup(false);
  };

  const componentToShow = (): JSX.Element => {
    switch (activeTab) {
      case CompanyTabEnum.FIND_CANDIDATES:
        return <MatchingProfile />;
      case CompanyTabEnum.JOB_POSTS:
        return <PostAJob />;
      case CompanyTabEnum.CULTURE_PROFILES:
        return <CultureProfile />;
      case CompanyTabEnum.CANDIDATE_OVERVIEW:
        return <CandidateOverview />;
      case CompanyTabEnum.CAREER_PAGE:
        return <CareerPage />;
      default:
        return <MatchingProfile />;
    }
  };

  const handleOpenAvatarChange = () => {
    setCompanyEditModal(!companyEditModal);
  };

  const handleSendInvitation = async (): Promise<void> => {
    if (inviteEmail && !inviteEmailError) {
      try {
        await InvitationsService.setInvitations({
          email: inviteEmail,
          role: 'recruiter'
        });
        setInviteEmail(''); // Clear the input after successful invitation
      } catch (error) {
        console.error('Error sending invitation:', error);
      }
    }
  };

  const companyHeaderToShow = isAdmin
    ? companyHeader
    : companyHeader.filter((item) => item.value !== CompanyTabEnum.CAREER_PAGE);

  return (
    <div
      className="companyPage"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <Navbar />
      <div
        className="company"
        style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <div className="company__top">
          <div className="company__top__left">
            <div className="company__top__left__image">
              {isAdmin && (
                <ActionButton
                  className="company__top__left__image-edit"
                  iconName="pencil.svg"
                  onClick={handleOpenAvatarChange}
                />
              )}

              <Image
                className="company__top__left-image"
                url={companies?.logo}
                name={'no-images.svg'}
                width={243}
                height={145}
                objectFit="contain"
              />
            </div>
            <div className="company__top__left__info">
              <span className="company__top__left__info-title">
                Welcome {me?.name}
              </span>
              <span className="company__top__left__info-subtitle">
                {companies?.name}
              </span>
              <span className="company__top__left__info-plan">
                {t('You are on the')}{' '}
                {String(companies?.subscription?.plan || 'Free')} plan
              </span>

              <span className="company__top__left__info-credit">
                {t('You can create')}{' '}
                {companies?.subscription?.current_limit?.jobs === 'unlimited'
                  ? `${t('unlimited job posts')} (${t('Fair Use Policy')})`
                  : `${companies?.subscription?.current_limit?.jobs} ${t('job posts')}`
                }{' '}
                {t(', unlock')}{' '}
                {companies?.subscription?.current_limit?.candidates}{' '}
                {t('candidate profiles')}{' '}
                {t('and use')}{' '}
                {companies?.subscription?.current_limit?.ai_credits}{' '}
                {t('AI credits')}
              </span>
              {isAdmin && (
                <div className="company__top__left__info__buttons">
                  <Button
                    onClick={() => navigate(COMPANY_ROUTES.SETTINGS)}
                    className="company__top__left__info__buttons-edit button"
                    title={t('Edit Company Info')}
                    t={t}
                  />

                  <Button
                    className="company__top__left__info__buttons-upgrade button"
                    onClick={() => navigate(COMPANY_ROUTES.PLANS)}
                    title="Upgrade"
                    t={t}
                  />
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="dashboard__right__top__left">
                <h1
                  className="dashboard__right__top__left-title"
                  onClick={() => {}}
                >
                  {t('invite colleagues')} <Image name="attention.svg" />
                </h1>
                {/* <p className="dashboard__right__top__left-desc">
                 {t('Upgrade or expand your account by inviting at least three of your friends to join you at recrout')}
                </p> */}

                <div className="dashboard__right__top__left__email">
                  <div className="inp">
                    <input
                      onInput={(e) =>
                        handleInviteInputChange(
                          (e.target as HTMLInputElement).value
                        )
                      }
                      className="dashboard__right__top__left__email-email disable-block"
                      type="email"
                      placeholder={t('email address')}
                    />
                    <Button
                      disabled={inviteEmail === '' || inviteEmailError !== ''}
                      t={t}
                      onClick={handleSendInvitation}
                      title="invite"
                      type="submit"
                      className={`dashboard__right__top__left__email-submit disable-block${
                        inviteEmail === '' || inviteEmailError !== ''
                          ? 'deactive'
                          : ''
                      }`}
                    />
                  </div>
                  {inviteEmailError && (
                    <span className="error">{inviteEmailError}</span>
                  )}
                </div>
                <div className="dashboard__right__top__left__email-links">
                  <Link
                    className="dashboard__right__top__left__email-invitations"
                    to={'/invitations/'}
                  >
                    All invitations
                  </Link>
                  <Link
                    className="dashboard__right__top__left__email-invitations"
                    to={'/colleagues/'}
                  >
                    All colleagues
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="company__header">
          {companyHeaderToShow.map((item, index) => (
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `company__header__item ${isActive ? 'active' : ''}`
              }
              key={index}
            >
              {item.title}
              <span className="company__header__item-line line"></span>
            </NavLink>
          ))}
        </div>
        <div
          className="main"
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          {componentToShow()}
        </div>
      </div>
      {showAvatarPopup && (
        <Popup onClose={handleClosePopup} title={t('Edit Your Profile Image')}>
          <AvatarPopup id={me.id} onPopupClose={handleClosePopup} t={t} />
        </Popup>
      )}
      {companyEditModal && (
        <Popup
          onClose={handleOpenAvatarChange}
          title={t('Edit Your Profile Image')}
        >
          <CompanyAvatar
            id={me.id}
            onPopupClose={handleOpenAvatarChange}
            t={t}
            selectedCompanyEdit={companies}
          />
        </Popup>
      )}
      <Footer />
    </div>
  );
};

export default Company;
